import { registerPlugin } from '@capacitor/core';
import { isNativeAndroid } from '@/lib/platform';

/**
 * A VALÓDI RENDSZERSÁV-INSET ANDROIDON, CSS-VÁLTOZÓBA.
 *
 * ⚠️ MÉRT HIBA javítása (2026-09-17, Geri két telefonja). A dokk helyét a
 * `--safe-bottom` adja, ami a CSS `env(safe-area-inset-bottom)`-ból jön. iOS-en
 * ez a home-indikátor, és jó. Androidon viszont NEM a navigációs sáv: ott a
 * safe-area a KIJELZŐ-KIVÁGÁST (lyuk, bevágás) tükrözi, tehát az érték 0 a
 * magas hárgombos sávos telefonon éppúgy, mint a vékony gesztuscsíkoson. A
 * dokk így mindkettőn a 4 px-es alsó korlátra esett: gesztuscsík alatt még
 * elment, gombsor alatt teljesen kitakarva.
 *
 * A számot CSS-ből nem lehet megszerezni, mert ott egyszerűen nincs meg. Az
 * ablak viszont pontosan tudja, készülékenként és pillanatonként — ezt olvassa
 * ki a `WindowInsetsPlugin`, és ez a modul írja be egy CSS-változóba.
 *
 * ⚠️ A CSS a KETTŐ MAXIMUMÁT veszi (`tokens.css`), nem cseréli le az `env()`-et:
 * iOS-en és böngészőben a natív érték nincs, ott tehát semmi nem változik.
 */

interface InsetValues {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface WindowInsetsPlugin {
  get(): Promise<InsetValues>;
  addListener(
    event: 'windowInsets',
    handler: (insets: InsetValues) => void,
  ): Promise<{ remove: () => Promise<void> }>;
}

const WindowInsets = registerPlugin<WindowInsetsPlugin>('WindowInsets');

const VARIABLES: Record<keyof InsetValues, string> = {
  top: '--android-inset-top',
  bottom: '--android-inset-bottom',
  left: '--android-inset-left',
  right: '--android-inset-right',
};

function apply(insets: InsetValues): void {
  const root = document.documentElement;
  for (const [key, variable] of Object.entries(VARIABLES)) {
    const value = insets[key as keyof InsetValues];
    /* Nem szám (régi natív réteg, hibás válasz) → inkább ne írjunk semmit,
       mint egy `NaNpx`-et, ami az egész `max()`-ot érvénytelenítené. */
    if (!Number.isFinite(value)) continue;
    root.style.setProperty(variable, `${Math.max(0, value).toFixed(2)}px`);
  }
}

/**
 * Bekapcsolja az inset-követést. Csak natív Androidon csinál bármit — máshol
 * csendben nem csinál semmit, mert ott az `env()` adja a helyes értéket.
 */
export function watchWindowInsets(): void {
  if (!isNativeAndroid()) return;

  /*
    ⚠️ KÉT IRÁNY, ÉS MINDKETTŐ KELL. A natív figyelő minden VÁLTOZÁST küld
    (forgatás, gesztus ⇄ gombsor váltás, billentyűzet), de az első elrendezésnél
    sül el — lehet, hogy még azelőtt, hogy ez a kód létezne. Ezért induláskor
    egyszer MI IS elkérjük az aktuális értéket.
  */
  void WindowInsets.get().then(apply).catch(() => undefined);
  void WindowInsets.addListener('windowInsets', apply).catch(() => undefined);
}

/**
 * EGY CSS-HOSSZ VALÓDI KÉPPONT-ÉRTÉKE.
 *
 * ⚠️ NEM `getComputedStyle(...).getPropertyValue('--safe-bottom')`. Egy egyéni
 * tulajdonság `@property`-regisztráció nélkül a SZÖVEGÉVEL számított: onnan
 * `max(env(safe-area-inset-bottom, 0px), var(--android-inset-bottom, 0px))`
 * jönne vissza, nem egy szám. A böngésző csak akkor oldja fel, ha egy valódi
 * tulajdonságba tesszük — ezért mérünk egy eldobható elemmel.
 */
function resolvePx(expression: string): number {
  if (typeof document === 'undefined' || !document.body) return -1;
  const probe = document.createElement('div');
  probe.style.cssText = `position:fixed;left:-9999px;bottom:0;width:0;visibility:hidden;height:${expression}`;
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return Math.round(height * 100) / 100;
}

/**
 * A HIBABEJELENTÉSBE KERÜLŐ ELRENDEZÉSI ADAT.
 *
 * ⚠️ KÜLÖN MUTATJA A KÉT FORRÁST, és pont ez a lényege. A dokk helyzetéről
 * eddig csak képernyőképből lehetett következtetni, hogy melyik forrásból jött
 * az érték. Innentől egy bejelentés megmondja: ha az `envBottom` 0, a
 * `nativeBottom` viszont 48, és a `safeBottom` is 48, akkor a natív út
 * dolgozik — ha a `safeBottom` 0 maradna, akkor nem.
 */
export function describeInsets(): Record<string, number> {
  /*
    ⚠️ DOM NÉLKÜL IS LE KELL FUTNIA. A hibabejelentés összeállítása nem
    dőlhet el azon, hogy van-e `document` — és a tesztek egy része Node
    környezetben fut, ahol nincs. A `-1` itt azt jelenti: „nem mérhető",
    ami megkülönböztethető a valódi nullától.
  */
  if (typeof document === 'undefined' || !document.documentElement) {
    return {
      safeTopPx: -1,
      safeBottomPx: -1,
      dockBottomPx: -1,
      envTopPx: -1,
      envBottomPx: -1,
      nativeTopPx: -1,
      nativeBottomPx: -1,
    };
  }

  const native = (name: string) => {
    const raw = document.documentElement.style.getPropertyValue(name);
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? value : -1;
  };
  return {
    safeTopPx: resolvePx('var(--safe-top)'),
    safeBottomPx: resolvePx('var(--safe-bottom)'),
    dockBottomPx: resolvePx('var(--dock-bottom)'),
    envTopPx: resolvePx('env(safe-area-inset-top, 0px)'),
    envBottomPx: resolvePx('env(safe-area-inset-bottom, 0px)'),
    nativeTopPx: native('--android-inset-top'),
    nativeBottomPx: native('--android-inset-bottom'),
  };
}
